import api, { API } from '@/utils/api.utils';

export interface Course {
  id: number;
  school_id: number;
  course_name: string;
  subject?: string;
  level?: string;
  subject_override?: string;
  level_override?: string;
  order_index: number;
}

export interface School {
  id: number;
  user_id: number;
  school_directory_id?: number;
  school_name: string;
  school_type?: string;
  city?: string;
  state?: string;
  country?: string;
  date_of_entry?: string;
  date_of_exit?: string;
  expect_to_graduate: boolean;
  is_boarding_school: boolean;
  graduated_early: boolean;
  graduated_late: boolean;
  took_time_off: boolean;
  took_gap_year: boolean;
  changed_schools: boolean;
  gpa_value?: number;
  gpa_max?: number;
  gpa_weighted: boolean;
  class_rank?: string;
  class_rank_type?: string;
  received_counselor_help: boolean;
  has_academic_honors: boolean;
  courses: Course[];
}

export interface CourseCreateRequest {
  course_name: string;
  subject?: string;
  level?: string;
  subject_override?: string;
  level_override?: string;
  order_index?: number;
}

export interface SchoolCreateRequest {
  school_directory_id?: number;
  school_name: string;
  school_type?: string;
  city?: string;
  state?: string;
  country?: string;
  date_of_entry?: string;
  date_of_exit?: string;
  expect_to_graduate?: boolean;
  is_boarding_school?: boolean;
  graduated_early?: boolean;
  graduated_late?: boolean;
  took_time_off?: boolean;
  took_gap_year?: boolean;
  changed_schools?: boolean;
  gpa_value?: number;
  gpa_max?: number;
  gpa_weighted?: boolean;
  class_rank?: string;
  class_rank_type?: string;
  received_counselor_help?: boolean;
  has_academic_honors?: boolean;
  courses?: CourseCreateRequest[];
}

export interface SchoolUpdateRequest {
  school_directory_id?: number;
  school_name?: string;
  school_type?: string;
  city?: string;
  state?: string;
  country?: string;
  date_of_entry?: string;
  date_of_exit?: string;
  expect_to_graduate?: boolean;
  is_boarding_school?: boolean;
  graduated_early?: boolean;
  graduated_late?: boolean;
  took_time_off?: boolean;
  took_gap_year?: boolean;
  changed_schools?: boolean;
  gpa_value?: number;
  gpa_max?: number;
  gpa_weighted?: boolean;
  class_rank?: string;
  class_rank_type?: string;
  received_counselor_help?: boolean;
  has_academic_honors?: boolean;
}

export interface CourseUpdateRequest {
  course_name?: string;
  subject?: string;
  level?: string;
  subject_override?: string;
  level_override?: string;
  order_index?: number;
}

export interface CourseAnalysisRequest {
  course_name: string;
}

export interface CourseAnalysisResponse {
  course_name: string;
  subject: string;
  level: string;
  confidence_score: number;
}

export interface SchoolsListResponse {
  schools: School[];
  total_count: number;
}

// Constants
export const SUBJECTS = [
  "Pre-Algebra", "Algebra", "Geometry", "Trigonometry", "Pre-Calculus", "Calculus", "Math (Other)",
  "Biology", "Chemistry", "Physics", "Earth/Environmental Science", "Science (Other)",
  "English", "History/Social Science", "Foreign/World Language", "Physical Education/Health",
  "Art (Visual or Performing)", "Computer Science", "Religion", "Other/Elective"
];

export const LEVELS = [
  "Regular/Standard", "Accelerated", "Advanced", "Advanced Placement (AP)",
  "AS/A-level/International A-level, Cambridge AICE", "College Prep", "Dual Enrollment",
  "Enriched", "GCSE, IGCSE", "Gifted", "High Honors", "Honors", "Intensive",
  "International Baccalaureate (IB)", "Pre-IB", "Regents", "N/A"
];

export const SCHOOL_TYPES = [
  "Public", "Private", "Charter", "Magnet", "International", "Religious", "Boarding", "Other"
];

class EducationService {
  private baseURL = `${API.BASE_URL}/api/v1/education`;

  /**
   * Analyze a course name to determine subject and level
   */
  async analyzeCourse(request: CourseAnalysisRequest): Promise<CourseAnalysisResponse> {
    try {
      const response = await api.post(`${this.baseURL}/courses/analyze`, request);
      return response.data;
    } catch (error) {
      console.error('Error analyzing course:', error);
      throw error;
    }
  }

  /**
   * Create a new school
   */
  async createSchool(schoolData: SchoolCreateRequest): Promise<School> {
    try {
      const response = await api.post(`${this.baseURL}/schools`, schoolData);
      return response.data;
    } catch (error) {
      console.error('Error creating school:', error);
      throw error;
    }
  }

  /**
   * Get all schools for the current user
   */
  async getUserSchools(): Promise<SchoolsListResponse> {
    try {
      const response = await api.get(`${this.baseURL}/schools`);
      return response.data;
    } catch (error) {
      console.error('Error fetching schools:', error);
      throw error;
    }
  }

  /**
   * Update an existing school
   */
  async updateSchool(schoolId: number, schoolData: SchoolUpdateRequest): Promise<School> {
    try {
      const response = await api.put(`${this.baseURL}/schools/${schoolId}`, schoolData);
      return response.data;
    } catch (error) {
      console.error('Error updating school:', error);
      throw error;
    }
  }

  /**
   * Delete a school
   */
  async deleteSchool(schoolId: number): Promise<void> {
    try {
      await api.delete(`${this.baseURL}/schools/${schoolId}`);
    } catch (error) {
      console.error('Error deleting school:', error);
      throw error;
    }
  }

  /**
   * Add a course to a school
   */
  async addCourseToSchool(schoolId: number, courseData: CourseCreateRequest): Promise<Course> {
    try {
      const response = await api.post(`${this.baseURL}/schools/${schoolId}/courses`, courseData);
      return response.data;
    } catch (error) {
      console.error('Error adding course:', error);
      throw error;
    }
  }

  /**
   * Update an existing course
   */
  async updateCourse(courseId: number, courseData: CourseUpdateRequest): Promise<Course> {
    try {
      const response = await api.put(`${this.baseURL}/courses/${courseId}`, courseData);
      return response.data;
    } catch (error) {
      console.error('Error updating course:', error);
      throw error;
    }
  }

  /**
   * Delete a course
   */
  async deleteCourse(courseId: number): Promise<void> {
    try {
      await api.delete(`${this.baseURL}/courses/${courseId}`);
    } catch (error) {
      console.error('Error deleting course:', error);
      throw error;
    }
  }
}

export default new EducationService();